import { activitiesFor } from "@/lib/learningGraph";
import { track } from "@/lib/track";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Volume2, VolumeX, BookOpen,
  CheckCircle2, Sparkles, RefreshCcw, X,
} from "lucide-react";
import { fetchLesson, tierForAgeGroup } from "@/lib/lessons";
import { findAcademySkillFor } from "@/lib/academyLink";
import { recordAttempt, isMastered } from "@/lib/mastery";
import { whyLineFor } from "@/lib/masteryEngine";
import { videoForLesson } from "@/data/lessonVideos";
import Skeleton from "@/components/ui/Skeleton";
import { useAgeGroup } from "@/lib/ageGroup";
import { pickIllustration } from "@/lib/illustrationBank";
import { getClassLink } from "@/lib/classLink";
import GeographyGate from "@/components/GeographyGate";
import AITeacher from "@/components/AITeacher";
import LessonSummaryCard from "@/components/LessonSummaryCard";
import LessonVideo from "@/components/LessonVideo";
import LessonVideos from "@/components/LessonVideos";
import TopicVideoRail from "@/components/TopicVideoRail";
import ReflectionPanel from "@/components/ReflectionPanel";
import ManipulativeEmbed from "@/components/tools/ManipulativeEmbed";
import ReadableText, { ReadAloudButton as RTButton } from "@/components/ReadableText";
import FocusShell from "@/components/FocusShell";
import { IdleNudge, BreakOffer } from "@/components/AttentionSupport";
import { setResumePoint, clearResumePoint } from "@/lib/planEngine";
import { setLessonStage, LESSON_STAGES } from "@/lib/lessonStageProgress";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/useSEO";
import { trackEvent, trackStartedActivity, trackCompletedActivity } from "@/lib/analytics";
import { explainAnotherWay, recordPersonalizationHelpful } from "@/lib/personalizationEngine";
import { setAttentionSetting, useAttentionSettings } from "@/lib/attentionSettings";
import { presentationSlides, renderDeckHtml } from "@/lib/lessonDeck";
import { useAppMode } from "@/lib/appMode";
import { journeyNextFor } from "@/lib/focusJourney";
import { useNextLesson } from "@/lib/useNextLesson";
import { useReadiness } from "@/lib/readiness";
import { emitFutureReadyEvent } from "@/lib/futureReady";
import MicroCheck from "@/components/learn/MicroCheck";
import { SampleQuestions } from "@/components/learn/SampleQuestions";
import { reportSupport } from "@/lib/humanSupport";
import { funnel } from "@/lib/learningFunnel";
import { juice } from "@/lib/juice";

// Base canonical origin — matches <Canonical /> so JSON-LD/OG stay in sync
// even when the app is served from a preview subdomain.
const CANONICAL_BASE = "https://myworld.quest";

// Subject → readable term for meta/JSON-LD (falls back to raw value).
const SUBJECT_LABEL = {
  maths: "Maths", math: "Maths", english: "English", science: "Science",
  geography: "Geography", history: "History", ict: "Computing",
  languages: "Languages", music: "Music", pe: "P.E.", pshe: "PSHE",
  re: "R.E.", art: "Art",
};

// Age tier → human label + typical range for schema.org LearningResource.
const TIER_META = {
  seedling: { label: "Ages 4–6",  edu: "beginner" },
  explorer: { label: "Ages 7–10", edu: "intermediate" },
  pioneer:  { label: "Ages 11–15", edu: "advanced" },
};

// Extract a clean ~155-char description from the lesson document.
// Prefers learning_objective, then first section body, then title fallback.
function buildLessonDescription(lesson) {
  const raw = (
    lesson.learning_objective ||
    (lesson.sections && lesson.sections[0]?.body) ||
    lesson.outcome ||
    lesson.title ||
    ""
  ).toString();
  const cleaned = raw.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 155) return cleaned;
  return cleaned.slice(0, 152).replace(/[,;:.\s]+\S*$/, "") + "…";
}

// Map the lesson document's age_tier ("seedling"/"explorer"/"pioneer")
// to the tier string AITeacher expects ("5-7"/"8-10"/"11-15").
const TIER_TO_AGE_BAND = {
  seedling: "5-7",
  explorer: "8-10",
  pioneer:  "11-15",
};

// Map LLM/UK-syllabus section types to the six canonical buckets the
// renderer knows how to style. Hoisted to module scope so React doesn't
// recreate it on every render.
const SECTION_TYPE_ALIASES = {
  starter: "intro",
  warmup: "intro",
  hook: "intro",
  main_teaching: "concept",
  teaching: "concept",
  direct_instruction: "concept",
  modelling: "example",
  guided_practice: "example",
  worked_example: "example",
  independent_practice: "check",
  practice: "check",
  check_for_understanding: "check",
  formative_check: "check",
  plenary: "recap",
  exit_ticket: "recap",
  closure: "recap",
};

/**
 * <LessonReader /> — full-screen textbook lesson experience.
 *
 * URL: /lesson/:code[?tier=explorer]
 *
 * Behaviours specified by the Phase 3 spec:
 *   • One section per screen (Intro → Concept → Example → Activity →
 *     Check → Recap), with a progress ring showing "Section 2 of 6".
 *   • "Read aloud" per section — pipes the section body through
 *     /api/tts using the same audio pipeline as AITeacher.
 *   • Key terms shown as tappable tokens that open a definition popover.
 *   • Check-your-understanding is interactive — right/wrong shown as
 *     gentle feedback ("Not quite — here's why…"), never a harsh X.
 *   • Recap ends with "✓ Mark as learned", "🎮 Practice with a game"
 *     and "→ Next lesson" buttons.
 *   • "Goes deeper" collapsed by default with a Want more? 🚀 toggle.
 *   • Falls back to the cached copy in localStorage when offline.
 */
// ── Command · Completion & Lesson Navigation · Phase 1 flag ─────────
// Reversible: set localStorage `ff_lesson_ending_v2` = "0" to restore the
// legacy EndPanel + ReflectionPanel end state. Default ON.
function lessonEndingV2() {
  try { return (localStorage.getItem("ff_lesson_ending_v2") ?? "1") !== "0"; }
  catch { return true; }
}

// Reversible: set `ff_lesson_focus_default` = "0" to open lessons in the
// full-chrome site view instead of focus mode. Default ON.
function lessonFocusDefault() {
  try { return (localStorage.getItem("ff_lesson_focus_default") ?? "1") !== "0"; }
  catch { return true; }
}

// Restate a lesson objective as an achieved, past-capability statement.
// Sourced verbatim from the lesson's own can_you_now / success_criteria /
// outcome — never fabricated. Kept ≤ ~140 chars, one sentence.
function achievedStatement(lesson) {
  const sc = lesson?.success_criteria;
  const cyn = lesson?.can_you_now;
  let src = String((Array.isArray(cyn) ? cyn[0] : (typeof cyn === "string" ? cyn : ""))
    || (Array.isArray(sc) ? sc[0] : (typeof sc === "string" ? sc : ""))
    || lesson?.outcome || lesson?.title || "").trim();
  // strip a leading emoji / non-word decoration
  src = src.replace(/^[^\w(]+/, "").trim();
  // normalise common lesson-objective stems to "You can now …"
  src = src
    .replace(/^by the end of this lesson[, ]*i (?:will (?:be able to|know|understand)|can)\s*/i, "")
    .replace(/^i (?:will be able to|can now|can|will|understand that|know that|understand|know)\s*/i, "")
    .replace(/^students? will be able to\s*/i, "")
    .replace(/^can you\s+/i, "")
    .replace(/\?\s*$/, "")
    .trim();
  if (!src) return "You finished this lesson.";
  const lead = /^you can now/i.test(src) ? "" : "You can now ";
  let out = lead + (lead ? src.charAt(0).toLowerCase() + src.slice(1) : src);
  if (out.length > 160) out = out.slice(0, 157).trimEnd() + "…";
  if (!/[.!?…]$/.test(out)) out += ".";
  return out;
}


export default function LessonReader() {
  const { code } = useParams();
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const { ageGroup } = useAgeGroup();
  const tier = params.get("tier") || tierForAgeGroup(ageGroup);
  const grownup = useAppMode() === "grownup";

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState(null);
  // Mid-activity resume: rehydrate `step` from a per-lesson checkpoint
  // key so leaving mid-way and returning restores position. Feb 2026
  // Phase B — attention-flow: PART 4 "PAUSE WITHOUT PENALTY".
  const CHECKPOINT_KEY = `mwq_lesson_step_${code || ""}`;
  const [step, setStep]   = useState(() => {
    try {
      const n = parseInt(localStorage.getItem(CHECKPOINT_KEY) || "0", 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch { return 0; }
  });
  const [openTerm, setOpenTerm] = useState(null);
  const [showDeeper, setShowDeeper] = useState(false);
  // Route change within the same mounted reader (Next lesson → /lesson/<other>): rehydrate step for the NEW code
  // instead of carrying the previous lesson's step over (which used to open the next lesson at its completion screen).
  const stepCodeRef = useRef(code);
  const stepResetPendingRef = useRef(false);
  useEffect(() => {
    if (stepCodeRef.current === code) return;
    stepCodeRef.current = code;
    stepResetPendingRef.current = true; // skip ONE persist pass so the old lesson's step is never written for the new code
    let n = 0;
    try { n = parseInt(localStorage.getItem(`mwq_lesson_step_${code || ""}`) || "0", 10); } catch { /* ignore */ }
    setStep(Number.isFinite(n) && n >= 0 ? n : 0);
    setShowDeeper(false);
  }, [code]);
  // Feb 2026 · Cycle 58 (Part 4) · Mobile action-overflow menu.
  const [actionsOpen, setActionsOpen] = useState(false);
  // Present mode overlay — renders the current lesson through the
  // shared deck engine (kidMode + readAloud) so the child can focus
  // on one page at a time with big type. ESC/Exit button/swipe-down
  // dismisses it back to the reader at the same step.
  const [presentOpen, setPresentOpen] = useState(false);
  const attention = useAttentionSettings();
  const focusOn = attention.focusMode === true;
  const toggleFocus = useCallback(() => {
    setAttentionSetting("focusMode", !focusOn);
    try { trackEvent(focusOn ? "lesson_focus_off" : "lesson_focus_on", { code }); } catch { /* ignore */ }
  }, [focusOn, code]);
  // Cycle 58 · Single-page ("Whole lesson") view. When true, we render
  // every section stacked in one flowing page instead of the paginated
  // step-by-step view. Great for revision, printing, or older learners
  // who prefer to skim. Persists across route re-mounts (localStorage
  // keyed by lesson code so different lessons remember independently).
  const SHOW_ALL_KEY = `we_lesson_show_all_v1:${code}`;
  const [showAll, setShowAll] = useState(() => {
    try { return localStorage.getItem(SHOW_ALL_KEY) === "1"; }
    catch { return false; }
  });
  const toggleShowAll = () => {
    setShowAll((v) => {
      const next = !v;
      try { localStorage.setItem(SHOW_ALL_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      try { trackEvent(next ? "lesson_view_whole" : "lesson_view_paginated", { code }); } catch { /* ignore */ }
      return next;
    });
  };
  // Cycle 3 · Plan Practice Shortcut. When we can match the lesson
  // title to an Academy skill, surface a "Practice in Academy" chip
  // so a curious kid jumps straight to the mastery ladder. Silent on
  // no-match.
  const [academyMatch, setAcademyMatch] = useState(null);
  useEffect(() => {
    if (!lesson || !lesson.subject || !lesson.title) return undefined;
    let dead = false;
    findAcademySkillFor({
      subject: lesson.subject,
      tier: (lesson.age_tier || "").startsWith("seed") ? "seedlings"
        : (lesson.age_tier || "").startsWith("pion") ? "pioneers" : "explorers",
      title: lesson.title,
    }).then((m) => { if (!dead) setAcademyMatch(m); });
    return () => { dead = true; };
  }, [lesson]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    fetchLesson(code, { ageTier: tier, language: "en" })
      .then((doc) => {
        if (cancelled) return;
        setLesson(doc);
        try { trackEvent("lesson_started", { code, subject: doc?.subject || "", tier: doc?.age_tier || tier }); } catch { /* ignore */ }
        // Cycle 58 · Activation-funnel middle event. Fires ONCE per
        // session — this is the real "the child started learning"
        // signal, not just "the child tapped somewhere".
        if (doc) {
          const strand = (doc.subject || "").toLowerCase() === "maths" ? "arithmetic"
            : (doc.subject || "").toLowerCase() === "english" ? "reading-writing"
            : "applied";
          try {
            trackStartedActivity({
              activity_id: `lesson/${doc.code || code}`,
              strand,
              source: "lesson-reader",
            });
          } catch { /* ignore */ }
        }
      })
      .catch((e)  => { if (!cancelled) setErr(e.message || "Could not load lesson"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [code, tier]);

  // Persist mid-activity checkpoint on every step change; when the
  // last section is reached we clear both the checkpoint AND any
  // resume point set by TodayHero so the "Continue" card disappears.
  useEffect(() => {
    if (!lesson || (lesson.code && lesson.code !== code)) return;
    if (stepResetPendingRef.current) { stepResetPendingRef.current = false; return; }
    try { localStorage.setItem(CHECKPOINT_KEY, String(step)); } catch { /* ignore */ }
    const total = (lesson.sections || []).length;

    // Cycle 60 · Continue-card real-%. Map the section step (0..total-1)
    // onto the 6-stage lesson flow so the Today "Continue" card can
    // render an accurate progress bar. Stages 1..5 come from linear
    // reading progress; stage 6 (Reward) is only reached once mastery
    // is claimed (handled separately in the mastery flow).
    if (total > 0) {
      const ratio = (step + 1) / total;
      const stage = step >= total - 1
        ? LESSON_STAGES.MASTERY
        : Math.max(LESSON_STAGES.LEARN, Math.min(LESSON_STAGES.MASTERY, Math.ceil(ratio * LESSON_STAGES.MASTERY)));
      setLessonStage(code, stage);
    }

    if (total > 0 && step >= total - 1) {
      try { localStorage.removeItem(CHECKPOINT_KEY); } catch { /* ignore */ }
      clearResumePoint();
    } else {
      // Keep global resume in sync so the MyLearning "Continue" card
      // points at this lesson while it is in progress.
      setResumePoint({
        to: `/lesson/${code}`,
        label: `${lesson.title || "Lesson"} · step ${step + 1} of ${total}`,
        meta: { code, step, total },
      });
    }
  }, [step, lesson, code, CHECKPOINT_KEY]);

  const sections = lesson?.sections || [];
  const total = sections.length;
  const cur = sections[step] || null;

  const percent = total ? Math.round(((step + 1) / total) * 100) : 0;

  // Phase 1 · completion is its own final card at index === total.
  // Steps 0..total-1 are content cards; step === total is the ending.
  const endingV2 = lessonEndingV2();
  const COMPLETION_STEP = total;               // the ending card index
  const isDone = endingV2 && !showAll && total > 0 && step >= COMPLETION_STEP;
  const onLastContent = !showAll && total > 0 && step === total - 1;
  // Count-down label: "3 more" · "2 more" · "Last one" (final card shows
  // the Finish button instead of a count).
  const remainingAfter = total - (step + 1);
  const countdownLabel = remainingAfter <= 0
    ? "Last one"
    : remainingAfter === 1 ? "Last one" : `${remainingAfter} more`;

  // Cycle 58 · Detect whether this lesson has a linked video so the
  // Watch anchor + the Print copy
  // all hide gracefully when there's nothing to watch. We check the
  // static catalog synchronously; the curated-video server fetch runs
  // later inside <LessonVideo /> and can only ADD a video, never
  // remove one — so this static check is a safe minimum bound.
  const hasVideo = useMemo(() => {
    if (!lesson) return false;
    // Curated-video localStorage cache (written by <LessonVideo />) —
    // gives an accurate signal on repeat visits before the fetch lands.
    try {
      const raw = localStorage.getItem(`mwq-lv-curated::${lesson.code || code}::${lesson.age_tier || ""}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.id) return true;
      }
    } catch { /* ignore */ }
    const v = videoForLesson({
      code: lesson.code || code,
      subject: lesson.subject,
      ageTier: lesson.age_tier,
      topic: lesson.topic || lesson.outcome,
    });
    return Boolean(v && v.id);
  }, [lesson, code]);

  // Cycle 58 · Share this lesson. Prefers native Web Share (mobile
  // share sheet) and falls back to copying the URL to the clipboard.
  // Never blocks the child — always resolves. Analytics fires on
  // success so we can measure share-driven acquisition later.
  const shareLesson = useCallback(async () => {
    if (!lesson) return;
    const shareUrl = typeof window !== "undefined"
      ? `${window.location.origin}/lesson/${encodeURIComponent(lesson.code || code)}?tier=${encodeURIComponent(lesson.age_tier || "explorer")}`
      : "";
    const title = `${lesson.title} — My World Quest`;
    const text = `A lesson worth sharing: ${lesson.title}. Learn together on My World Quest.`;
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({ title, text, url: shareUrl });
        try { trackEvent("lesson_shared", { code: lesson.code || code, method: "native" }); } catch { /* ignore */ }
        toast.success("Thanks for sharing!");
        return;
      }
    } catch (err) {
      // Fall through to clipboard fallback on abort/reject.
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — share it anywhere!");
      try { trackEvent("lesson_shared", { code: lesson.code || code, method: "clipboard" }); } catch { /* ignore */ }
    } catch {
      toast.error("Could not share — copy the URL from the address bar.");
    }
  }, [lesson, code]);

  // Cycle 13.5 · Memoise the CheckQuestions completion callback so
  // it doesn't create a fresh function every render — helps prevent
  // any downstream child from spuriously re-mounting and clobbering
  // state (the suspected root cause of the "Next stuck at 3/4"
  // report against Geography lessons).
  const handleCheckComplete = useCallback((score) => {
    recordAttempt(code, score, 5);
  }, [code]);

  // Extra defensive: hoist the Next handler out of the JSX inline
  // arrow so a re-render of the button row can't clobber the
  // functional updater between mount cycles.
  const goNext = useCallback(() => {
    setStep((s) => Math.min(endingV2 ? total : total - 1, s + 1));
  }, [total, endingV2]);
  const goPrev = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  // Scroll so the lesson body lands just under the sticky lesson
  // header on every step change — keeping the "lesson box" pinned
  // near the top of the viewport rather than falling below the
  // amber header. rAF lets the AnimatePresence transition commit
  // first so measurements are accurate.
  useEffect(() => {
    if (typeof window === "undefined") return;
    requestAnimationFrame(() => {
      try {
        const body = document.getElementById("lesson-body");
        const header = document.getElementById("lesson-sticky-header");
        if (body) {
          const headerH = header ? header.getBoundingClientRect().height : 0;
          const top = body.getBoundingClientRect().top + window.scrollY - headerH - 8;
          window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch { window.scrollTo(0, 0); }
    });
  }, [step, code, tier]);

  // ─── SEO · Per-lesson meta tags + JSON-LD LearningResource ────────
  // Fires when the lesson doc lands so every /lesson/:code URL gets
  // a unique title, description, canonical OG image and Google-
  // rich-result-eligible structured data. Falls back gracefully when
  // fields are missing.
  const seoTitle = lesson
    ? `${lesson.title} — ${SUBJECT_LABEL[lesson.subject] || lesson.subject || "Lesson"} | My World Quest`
    : null;
  const seoDescription = lesson ? buildLessonDescription(lesson) : null;
  const seoUrl = `${CANONICAL_BASE}/lesson/${code || ""}`;
  const seoJsonLd = useMemo(() => {
    if (!lesson) return null;
    const tierMeta = TIER_META[lesson.age_tier] || {};
    const subjectLabel = lesson.subject ? (SUBJECT_LABEL[lesson.subject] || lesson.subject) : null;
    const crumbs = [{ name: "Home", href: "/" }, { name: "Subjects", href: "/subjects" }, ...(subjectLabel ? [{ name: subjectLabel, href: `/subjects/${encodeURIComponent(subjectLabel)}` }] : []), { name: lesson.title, href: seoUrl }];
    return [breadcrumbLd(crumbs), {
      "@context": "https://schema.org",
      "@type": "LearningResource",
      name: lesson.title,
      description: buildLessonDescription(lesson),
      url: seoUrl,
      inLanguage: lesson.language || "en",
      isFamilyFriendly: true,
      audience: {
        "@type": "EducationalAudience",
        educationalRole: "student",
        ...(tierMeta.label ? { audienceType: tierMeta.label } : {}),
      },
      learningResourceType: "Lesson",
      educationalLevel: tierMeta.edu || "beginner",
      about: lesson.subject
        ? { "@type": "Thing", name: SUBJECT_LABEL[lesson.subject] || lesson.subject }
        : undefined,
      teaches: lesson.learning_objective || lesson.outcome || undefined,
      timeRequired: lesson.estimated_minutes ? `PT${lesson.estimated_minutes}M` : undefined,
      isAccessibleForFree: true,
      publisher: {
        "@type": "Organization",
        name: "My World Quest",
        url: CANONICAL_BASE,
      },
    }];
  }, [lesson, seoUrl]);
  useSeo({
    title: seoTitle,
    description: seoDescription,
    ogImage: "/share-poster.png",
    url: seoUrl,
    jsonLd: seoJsonLd,
  });

  if (loading) {
    // Same FocusShell chrome as the loaded lesson → no header swap → no layout shift (CLS gate).
    return (
      <FocusShell title="Loading lesson…" force={lessonFocusDefault()} onExit={() => navigate(-1)}>
      <div className="max-w-3xl mx-auto px-4 py-4 md:py-8 min-h-[80vh]" data-testid="lesson-loading">
        {/* Feb 2026 · skeleton stack replaces the bare "Loading lesson…"
            text so kids see the lesson shape land instantly. */}
        <div className="rounded-2xl border-4 border-slate-900 bg-white p-4 md:p-6 shadow-[6px_6px_0_0_rgba(15,23,42,1)] space-y-3">
          <Skeleton w="30%" h="0.75rem" />
          <Skeleton w="80%" h="1.75rem" />
          <Skeleton w="60%" h="0.75rem" />
          <div className="mt-4 space-y-2">
            <Skeleton w="100%" h="0.75rem" />
            <Skeleton w="100%" h="0.75rem" />
            <Skeleton w="90%" h="0.75rem" />
          </div>
        </div>
      </div>
      </FocusShell>
    );
  }
  if (err || !lesson) {
    // "Lesson coming soon" only makes sense when we have a real code.
    // If code itself is undefined (means a broken link somewhere fed us
    // a `to="/lesson/undefined"`) we tell the child something warm and
    // route them somewhere useful instead of hard-blocking with an
    // "undefined" placeholder they can't act on.
    const knownCode = code && code !== "undefined";
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="lesson-missing">
        {/* Back button removed — Layout's GlobalBackHome strip covers this. */}
        <div className="mt-6 text-6xl mb-2">🧭</div>
        <h1 className="font-heading text-3xl font-bold">
          {knownCode ? "Let's find you a lesson" : "This lesson is loading somewhere else"}
        </h1>
        <p className="mt-2 text-slate-600 font-bold">
          {knownCode ? (
            <>
              This one isn&apos;t here — but there&apos;s plenty to explore.
              Jump to the topic page and pick a lesson to start.
            </>
          ) : (
            <>Pick a topic below to jump straight to the syllabus and find a lesson to start.</>
          )}
        </p>
        <Link
          to={knownCode ? `/learn-topics?s=&open=${encodeURIComponent(code)}` : "/learn-topics"}
          className="mt-4 inline-flex items-center gap-1 h-10 px-4 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold shadow-[0_3px_0_0_rgba(15,23,42,1)]"
          data-testid="lesson-fallback-link"
        >
          {knownCode ? "Open topic outline" : "Browse topics"}
          <ChevronRight size={16} strokeWidth={3} />
        </Link>
      </div>
    );
  }

  return (
    <FocusShell
      title={lesson.title}
      progress={{ current: Math.min(step + 1, total), total }}
      steps={sections.map((sec, i) => sec?.title || sec?.heading || `Part ${i + 1}`)}
      onExit={() => navigate("/my-learning")}
      force={lessonFocusDefault()}
      actions={
        <div className="relative">
          <button
            type="button"
            onClick={() => setActionsOpen((v) => !v)}
            aria-label="Lesson actions"
            aria-expanded={actionsOpen}
            data-testid="focus-actions-toggle"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full border-2 border-slate-900 bg-white font-bold text-lg shadow-[0_2px_0_0_rgba(15,23,42,1)]"
          >
            ⋯
          </button>
          {actionsOpen && (
            <div
              className="absolute right-0 mt-1 w-52 rounded-2xl border-[3px] border-slate-900 bg-white p-1.5 shadow-[0_4px_0_0_rgba(15,23,42,1)] z-50"
              role="menu"
              data-testid="focus-actions-menu"
            >
              <MenuButton onClick={() => { setActionsOpen(false); setPresentOpen(true); }} testId="focus-present" icon="🎬">Present mode</MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); window.print(); }} testId="focus-print" icon="🖨️">Print / Save PDF</MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); shareLesson(); }} testId="focus-share" icon="🔗">Share lesson</MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); toggleShowAll(); }} testId="focus-showall" icon={showAll ? "📖" : "📚"}>
                {showAll ? "One at a time" : "See whole lesson"}
              </MenuButton>
              {academyMatch && (
                <MenuButton onClick={() => { setActionsOpen(false); navigate(academyMatch.to); }} testId="focus-practice-academy" icon="🎯">
                  Practice in Academy
                </MenuButton>
              )}
            </div>
          )}
        </div>
      }
    >
    <IdleNudge />
    <BreakOffer />
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 md:py-8" data-testid="lesson-reader">
      {/* Back button removed — Layout's GlobalBackHome strip covers this. */}

      {/* Header — Feb 2026 · Cycle 58 (Part 4) · Further compressed.
          On mobile the action chips collapse into a "•••" popover so
          the amber tile only carries the essentials: subject line +
          title + progress. Chips remain inline on md+ where there's
          room. Combined with the previous pass, mobile hero is now
          ~150 px (down from ~430 px before Cycle 58). */}
      <header id="lesson-sticky-header" className="mt-2 sticky top-[104px] md:top-[128px] z-20 rounded-2xl border-4 border-slate-900 bg-amber-200 p-2.5 md:p-5 shadow-[6px_6px_0_0_rgba(15,23,42,1)] relative">
        {/* Overflow menu button (mobile-only) — sits at top-right of
            the tile so it never adds a row. */}
        <div className="absolute top-2 right-2 md:hidden">
          <button
            type="button"
            onClick={() => setActionsOpen((v) => !v)}
            aria-label="Lesson actions"
            aria-expanded={actionsOpen}
            data-testid="lesson-actions-toggle"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border-2 border-slate-900 bg-white font-bold text-lg shadow-[0_2px_0_0_rgba(15,23,42,1)] no-print"
          >
            ⋯
          </button>
          {actionsOpen && (
            <div
              className="absolute right-0 mt-1 w-52 rounded-2xl border-[3px] border-slate-900 bg-white p-1.5 shadow-[0_4px_0_0_rgba(15,23,42,1)] z-30 no-print"
              role="menu"
              data-testid="lesson-actions-menu"
            >
              <MenuButton onClick={() => { setActionsOpen(false); setPresentOpen(true); }} testId="lesson-present-mob" icon="🎬">Present mode</MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); toggleFocus(); }} testId="lesson-focus-mob" icon={focusOn ? "🎯" : "🎯"}>
                {focusOn ? "Focus mode: on" : "Focus mode"}
              </MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); window.print(); }} testId="lesson-print-mob" icon="🖨️">Print / Save PDF</MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); shareLesson(); }} testId="lesson-share-mob" icon="🔗">Share lesson</MenuButton>
              <MenuButton onClick={() => { setActionsOpen(false); toggleShowAll(); }} testId="lesson-toggle-showall-mob" icon={showAll ? "📖" : "📚"}>
                {showAll ? "One at a time" : "See whole lesson"}
              </MenuButton>
              {academyMatch && (
                <MenuButton onClick={() => { setActionsOpen(false); navigate(academyMatch.to); }} testId="lesson-practice-in-academy-mob" icon="🎯">
                  Practice in Academy
                </MenuButton>
              )}
            </div>
          )}
        </div>

        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 pr-10 md:pr-0" data-testid="lesson-eyebrow">
          {grownup ? `${lesson.subject} · ${lesson.curriculum || "Global curriculum"} · ${lesson.age_tier}` : lesson.subject}
        </p>
        {grownup && Array.isArray(lesson.curricula) && lesson.curricula.length > 0 && (
          <p className="mt-1 hidden md:flex flex-wrap gap-1" data-testid="lesson-curricula">
            {lesson.curricula.slice(0, 6).map((c) => (
              <span key={c} className="inline-flex items-center h-6 px-2 rounded-full bg-white/70 border border-slate-900/40 text-[10px] font-bold text-slate-700">
                {c}
              </span>
            ))}
          </p>
        )}
        <h1 className="font-heading text-lg md:text-3xl font-bold leading-tight mt-0.5 md:mt-1 pr-10 md:pr-0" data-testid="lesson-title">
          {lesson.title}
        </h1>

        {/* Action chips — hidden on mobile (moved into the ⋯ menu),
            still full-labeled on md+ where there's plenty of room. */}
        <div className="hidden md:flex mt-2 flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPresentOpen(true)}
            data-testid="lesson-present"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-fuchsia-200 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform no-print"
            aria-label="Open this lesson in full-screen present mode"
          >
            🎬 Present
          </button>
          <button
            type="button"
            onClick={toggleFocus}
            data-testid="lesson-focus"
            aria-pressed={focusOn}
            className={`inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform no-print ${focusOn ? "bg-emerald-300" : "bg-white"}`}
            aria-label="Toggle focus mode"
          >
            🎯 {focusOn ? "Focus: on" : "Focus"}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="lesson-print"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform no-print"
            aria-label="Print this lesson or save it as a PDF"
          >
            🖨️ Print / Save PDF
          </button>
          <button
            type="button"
            onClick={shareLesson}
            data-testid="lesson-share"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-sky-100 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform no-print"
            aria-label="Share this lesson"
          >
            🔗 Share lesson
          </button>
          <button
            type="button"
            onClick={toggleShowAll}
            data-testid="lesson-toggle-showall"
            className={`inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform no-print ${showAll ? "bg-emerald-200" : "bg-yellow-100"}`}
            aria-pressed={showAll}
          >
            {showAll ? "📖 One at a time" : "📚 See whole lesson"}
          </button>
          {academyMatch && (
            <Link
              to={academyMatch.to}
              data-testid="lesson-practice-in-academy"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-indigo-200 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
            >
              🎯 Practice · <span className="truncate max-w-[16ch]">{academyMatch.skill.name}</span>
            </Link>
          )}
        </div>

        {/* Compact progress — count-down label + slim bar. Hidden on the
            completion card (which shows its own delta). */}
        {!showAll && !isDone && (
          <div className="mt-2 flex items-center gap-2">
            <span
              className="text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-700 whitespace-nowrap"
              data-testid="lesson-progress-label"
            >
              {onLastContent ? "Last one" : countdownLabel}
            </span>
            <div className="flex-1 h-1.5 rounded-full border-2 border-slate-900 bg-white overflow-hidden">
              <div
                className="h-full bg-emerald-400 transition-[width]"
                style={{ width: `${percent}%` }}
                data-testid="lesson-progress-bar"
              />
            </div>
            <span className="text-[10px] md:text-xs font-black tabular-nums text-slate-700 whitespace-nowrap">
              {step + 1}/{total}
            </span>
          </div>
        )}
      </header>

      {/* Companion summary — Phase 1 · "For grown-ups" disclosure, card 1
          only, closed by default. Hidden on completion + later cards. */}
      {!isDone && step === 0 && (
      <details className="mt-4 rounded-2xl border-2 border-slate-200 bg-white/70" data-testid="lesson-grownups-summary">
        <summary className="cursor-pointer select-none px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-500">
          For grown-ups
        </summary>
        <div className="px-2 pb-2">
          <LessonSummaryCard
            code={lesson.code || code}
            outcome={lesson.outcome || lesson.title || lesson.topic}
            subject={lesson.subject || "general"}
            ageTier={lesson.age_tier}
          />
        </div>
      </details>
      )}

      {/* Mode tabs removed (Phase 1 · L3) — they looked live but did
          nothing. Reading is the lesson; Practice lives on the completion
          card. */}

      {/* Body — one section per screen (or all-stacked when the
          "See whole lesson" toggle is on). Both branches use the
          same SectionRenderer so questions and check-flows behave
          identically in either view. */}
      <div id="lesson-body" className="mt-6 min-h-[240px]">
        {isDone ? (
          <CompletionScreen
            lesson={lesson}
            code={code}
            tier={tier}
            contentTotal={total}
            academyMatch={academyMatch}
            navigate={navigate}
            onReplay={() => setStep(0)}
          />
        ) : showAll ? (
          <div className="space-y-4" data-testid="lesson-all-sections">
            {sections.map((s, i) => (
              <section
                key={`all-${i}`}
                className="rounded-3xl border-4 border-slate-900 bg-white p-5 md:p-6 shadow-[6px_6px_0_0_rgba(15,23,42,1)] max-w-[75ch] mx-auto"
                data-testid={`lesson-all-section-${i}`}
              >
                <SectionRenderer
                  section={s}
                  onTermClick={setOpenTerm}
                  onCheckComplete={handleCheckComplete}
                  subject={lesson.subject}
                />
              </section>
            ))}
          </div>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            {cur && (
              <motion.section
                key={step}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
                // Body constrained to 65ch reading rhythm per design
                // guidelines (Lesson Reader → line-length rule).
                className="rounded-3xl border-4 border-slate-900 bg-white p-5 md:p-6 shadow-[6px_6px_0_0_rgba(15,23,42,1)] max-w-[75ch] mx-auto"
                data-testid={`lesson-section-${cur.type}`}
              >
                <SectionRenderer
                  section={cur}
                  onTermClick={setOpenTerm}
                  onCheckComplete={handleCheckComplete}
                  subject={lesson.subject}
                />
              </motion.section>
            )}
          </AnimatePresence>
        )}
        {/* Have a go — 3 bank questions for THIS objective right after the first card, so a lesson never starts as theory only.
            Same bank as Practice/Check; renders nothing when the bank is empty. Ledger kind "sample" (never mastery). */}
        {!isDone && (showAll || step === 0) && (
          <SampleQuestions
            className="mt-4 max-w-[75ch] mx-auto"
            outcomeCodes={[code]}
            tier={lesson?.age_tier || tier}
            skillSlug={code}
            title="Have a go — 3 quick questions"
            practiceLabel="Practise this topic"
            masterLabel={(lesson?.age_tier || tier) !== "seedling" ? "Take the check" : undefined}
            ledger={{ skillId: `objective:${code}`, skillType: String(lesson?.subject || "").toLowerCase() || null }}
            onPractice={() => navigate(`/practice/${code}?age_tier=${lesson?.age_tier || tier}`)}
            onMaster={(lesson?.age_tier || tier) !== "seedling" ? () => navigate(`/learn-topics/${code}/test?age_tier=${lesson?.age_tier || tier}`) : undefined}
          />
        )}
        {/* Lightweight micro-check mid-lesson (bank question for THIS objective; renders nothing when the bank is empty). */}
        {!isDone && !showAll && cur && total > 1 && step === Math.max(1, Math.floor(total / 2)) && (
          <MicroCheck code={code} tier={lesson?.age_tier || tier} subject={String(lesson?.subject || "").toLowerCase()} />
        )}
      </div>

      {/* Feb 2026 · Print-only full-lesson dump. When the child taps
          "Print / Save PDF" the browser reveals this block (see the
          print stylesheet in index.css: `.print-only { display:none }`
          collapses to `display:block` inside @media print). Every
          section renders on paper regardless of the current on-screen
          step. */}
      <div className="print-only-block" data-testid="lesson-print-all" aria-hidden>
        {sections.map((s, i) => (
          <section
            key={`print-${i}`}
            className="print-section rounded-2xl border-2 border-slate-900 bg-white p-4 mt-4"
            data-testid={`lesson-print-section-${i}`}
          >
            <SectionRenderer
              section={s}
              onTermClick={() => {}}
              onCheckComplete={() => {}}
              subject={lesson.subject}
            />
          </section>
        ))}
      </div>

      {/* Section navigator — hidden in single-page mode and on the
          completion card. */}
      {!showAll && !isDone && (
        <nav
          className="mt-4 flex flex-wrap items-center justify-between gap-2 min-w-0"
          aria-label="Lesson section navigation"
        >
        <button
          type="button"
          disabled={step === 0}
          onClick={goPrev}
          data-testid="lesson-prev"
          aria-label="Previous card"
          className="inline-flex items-center gap-1 h-11 min-h-11 px-3 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40"
        >
          <ChevronLeft size={16} strokeWidth={3} /> Previous
        </button>
        <div className="order-3 sm:order-none w-full sm:w-auto flex items-center justify-center gap-2 min-w-0" aria-hidden>
          {/* Count-down text so children can answer "how much is left?"
              at a glance. Final card reads "Last one". */}
          <span
            className="text-[11px] font-black uppercase tracking-widest text-slate-600 tabular-nums"
            data-testid="lesson-progress"
          >
            {onLastContent ? "Last one" : countdownLabel}
          </span>
          <span className="flex flex-wrap gap-1 max-w-[50vw]">
            {sections.map((_, i) => (
              <span
                key={i}
                className={`h-2 w-2 rounded-full border-2 border-slate-900 ${
                  i === step ? "bg-amber-400" : i < step ? "bg-emerald-300" : "bg-white"
                }`}
              />
            ))}
          </span>
        </div>
        <button
          type="button"
          disabled={endingV2 ? step >= total : step >= total - 1}
          onClick={goNext}
          data-testid="lesson-next"
          className={"inline-flex items-center gap-1 h-11 min-h-11 px-3 rounded-full border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40 " + (endingV2 && onLastContent ? "bg-emerald-300" : "bg-amber-300")}
        >
          {endingV2 && onLastContent
            ? (<><CheckCircle2 size={16} strokeWidth={3} /> Finish ✓</>)
            : (<>Next <ChevronRight size={16} strokeWidth={3} /></>)}
        </button>
      </nav>
      )}

      {/* Legacy end state — only when the Phase-1 ending flag is OFF.
          When ON, the completion screen renders inside #lesson-body. */}
      {!endingV2 && (showAll || step === total - 1) && (
        <>
          <EndPanel lesson={lesson} code={code} navigate={navigate}
                   showDeeper={showDeeper} setShowDeeper={setShowDeeper} />
          <ReflectionPanel
            code={lesson.code || code}
            outcome={lesson.outcome || lesson.title}
            subject={lesson.subject}
            ageTier={lesson.age_tier}
          />
        </>
      )}
      {/* PHASE1-COMPLETION-BOUNDARY — nothing child-facing may render
          below the completion screen when endingV2 is on. The blocks
          that follow are all gated on !isDone (lint enforces this). */}
      {!isDone && (<>
      {/* Kid-safe explainer video — falls back to null if there's no
          curated video for this subject × tier, so lessons without
          one still get the AI Teacher as the audio-first alternative. */}
      <LessonVideo
        code={lesson.code || code}
        subject={lesson.subject}
        ageTier={lesson.age_tier}
        topic={lesson.topic || lesson.title}
      />

      {/* Server-backed shelf — pulls every kid-safe video that admins
          have attached to this topic × tier from the `topic_videos`
          collection. Renders nothing when empty, so lessons without
          attachments look identical to before. */}
      <div id="lesson-video">
        <TopicVideoRail
          topic={lesson.topic || lesson.title}
          tier={lesson.age_tier}
          subject={lesson.subject}
          lessonCode={lesson.code || code}
        />
      </div>

      {/* Channel-search fallback — 3-4 trusted YouTube channels for
          this subject open in a new tab scoped to a topic-specific
          search. Guarantees every lesson (even those without a
          curated embed) has multiple video discovery paths. */}
      <div className="mt-4">
        <LessonVideos
          subject={lesson.subject}
          lessonTitle={lesson.title}
          outcome={lesson.outcome}
          lessonCode={lesson.code || code}
          tier={tier}
        />
      </div>

      {/* ✨ Refresh lesson — one-tap escape hatch that regenerates the
          lesson body via Claude and caches the result in Mongo forever
          after. Kids see the seeded template on first visit; a parent
          or teacher tapping this once "upgrades" the lesson for every
          future visitor to that (code, tier, language). Rate-limited
          server-side to 1/minute so nobody can burn LLM budget. */}
      {lesson.content_source !== "llm" && (
        <div className="mt-3 rounded-2xl border-[3px] border-dashed border-amber-400 bg-amber-50 p-3" data-testid="lesson-regenerate">
          <p className="text-[11px] font-bold text-amber-900">
            📝 This lesson uses starter content. Refresh to generate a full teacher-authored version.
          </p>
          <button
            type="button"
            data-testid="lesson-regenerate-btn"
            onClick={async () => {
              try {
                const API = process.env.REACT_APP_BACKEND_URL;
                const r = await fetch(`${API}/api/lessons/${encodeURIComponent(lesson.code || code)}/regenerate?age_tier=${encodeURIComponent(lesson.age_tier)}&language=${encodeURIComponent(lesson.language || "en")}`, { method: "POST" });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                window.location.reload();
              } catch (e) {
                alert(`Refresh failed: ${e.message}. Please try again in a moment.`);
              }
            }}
            className="mt-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-[2.5px] border-slate-900 bg-yellow-300 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-[1px] transition-all"
          >
            ✨ Refresh this lesson
          </button>
        </div>
      )}
      </>)}

      {/* Key term popover */}
      <AnimatePresence>
        {openTerm && (
          <TermPopover term={openTerm} onClose={() => setOpenTerm(null)} />
        )}
      </AnimatePresence>

      {/* Floating AI Teacher — chat + friendly voice-read companion for
          this specific lesson outcome. Panel opens on tap; auto-loads a
          warm intro cached server-side by (code + age_tier). */}
      <AITeacher
        code={lesson.code || code}
        outcome={lesson.outcome || lesson.title || lesson.topic || `Lesson ${lesson.code || code}`}
        subject={lesson.subject || "general"}
        ageTier={TIER_TO_AGE_BAND[lesson.age_tier] || "8-10"}
      />
    </div>
    {presentOpen && (
      <PresentOverlay
        lesson={lesson}
        step={step}
        onClose={() => setPresentOpen(false)}
      />
    )}
    </FocusShell>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Section renderer — swaps templates per section.type
// ────────────────────────────────────────────────────────────────────
function SectionRenderer({ section, onTermClick, onCheckComplete, subject = "" }) {
  const rawType = section.type;
  // Normalise LLM/UK-syllabus section types to the six canonical
  // buckets. Map lives at module scope (see SECTION_TYPE_ALIASES) —
  // was the single source of the "blank plenary / missing check
  // questions" bug pre-Feb-2026.
  const type = SECTION_TYPE_ALIASES[rawType] || rawType || "concept";
  const heading = section.heading || defaultHeadingFor(type);
  const body = section.body || "";
  const imgPrompt = section.image_prompt || section.diagram_prompt || null;

  // Some LLM sections carry a `prompt` (starter) or `worked_example`
  // (main_teaching) instead of extra body copy — surface them so the
  // section never renders empty.
  const prompt = section.prompt || null;
  const workedExample = section.worked_example || null;
  const reflectionPrompt = section.reflection_prompt || null;

  // Plenary/exit-ticket sections carry a single-question object; the
  // recap-style renderer converts it into the same CheckQuestions
  // widget kids already know so the "plenary" screen isn't blank.
  const exitTicket = section.exit_ticket || null;

  // Merge exit_ticket into the questions array so the shared
  // CheckQuestions widget handles both `check` and `recap` sections
  // that ship a single reflective MCQ.
  const combinedQuestions = useMemo(() => {
    const qs = Array.isArray(section.questions) ? [...section.questions] : [];
    if (exitTicket && exitTicket.q) qs.push(exitTicket);
    return qs;
  }, [section.questions, exitTicket]);

  return (
    <>
      <div className="flex items-baseline gap-2">
        <SectionEmoji type={type} />
        <h2 className="font-heading text-2xl md:text-3xl font-bold leading-tight">{heading}</h2>
      </div>

      {imgPrompt && (
        <IllustrationSlot
          prompt={imgPrompt}
          type={type}
          heading={heading}
          keyTerms={section.key_terms}
          illustrationUrl={section.illustration_url}
        />
      )}

      {body && (
        <div className="mt-4">
          <RTButton text={`${heading}. ${body}`} testid="lesson-read-aloud" />
          <p className="mt-2 text-base md:text-lg leading-relaxed whitespace-pre-line text-slate-800" data-testid="lesson-body">
            {highlightTerms(body, section.key_terms, onTermClick)}
          </p>
        </div>
      )}

      {workedExample && (
        <div className="mt-4 rounded-2xl border-[3px] border-slate-900 bg-amber-50 p-3 md:p-4" data-testid="lesson-worked-example">
          <p className="font-heading text-base md:text-lg font-bold flex items-center gap-1">
            <Sparkles size={16} strokeWidth={3} /> Worked example
          </p>
          <p className="mt-1 text-sm md:text-base font-medium text-slate-800 whitespace-pre-line">
            {workedExample}
          </p>
        </div>
      )}

      {prompt && (
        <div className="mt-4 rounded-2xl border-[3px] border-slate-900 bg-sky-50 p-3 md:p-4" data-testid="lesson-prompt">
          <p className="font-heading text-base md:text-lg font-bold flex items-center gap-1">
            💭 Think about this
          </p>
          <p className="mt-1 text-sm md:text-base font-medium text-slate-800 whitespace-pre-line">
            {prompt}
          </p>
        </div>
      )}

      {section.key_terms?.length ? (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="lesson-key-terms">
          {section.key_terms.map((kt, i) => (
            <button
              key={i} type="button"
              onClick={() => onTermClick(kt)}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border-2 border-slate-900 bg-amber-100 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]"
            >
              <Sparkles size={12} strokeWidth={3} /> {kt.term}
            </button>
          ))}
        </div>
      ) : null}

      {section.instructions && (
        <div className="mt-4 rounded-2xl border-[3px] border-slate-900 bg-emerald-50 p-3 md:p-4">
          <p className="font-heading text-base md:text-lg font-bold flex items-center gap-1">
            <BookOpen size={16} strokeWidth={3} /> Try it yourself
          </p>
          <p className="mt-1 text-sm md:text-base font-medium text-slate-800 whitespace-pre-line">
            {section.instructions}
          </p>
        </div>
      )}

      {section.manipulative && section.manipulative.slug && (
        <ManipulativeEmbed
          slug={section.manipulative.slug}
          config={section.manipulative.config || ""}
        />
      )}

      {type === "check" && combinedQuestions.length > 0 && (
        <CheckQuestions questions={combinedQuestions} onComplete={onCheckComplete} subject={subject} />
      )}

      {type === "recap" && Array.isArray(section.remember_this) && (
        <ul className="mt-4 space-y-1.5" data-testid="lesson-remember">
          {section.remember_this.map((line, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 size={18} className="mt-0.5 text-emerald-600 shrink-0" strokeWidth={3} />
              <span className="font-bold text-slate-800">{line}</span>
            </li>
          ))}
        </ul>
      )}

      {type === "recap" && combinedQuestions.length > 0 && (
        <CheckQuestions questions={combinedQuestions} onComplete={onCheckComplete} subject={subject} />
      )}

      {reflectionPrompt && (
        <div className="mt-4 rounded-2xl border-[3px] border-slate-900 bg-emerald-50 p-3 md:p-4" data-testid="lesson-reflection">
          <p className="font-heading text-base md:text-lg font-bold flex items-center gap-1">
            🌱 Reflect
          </p>
          <p className="mt-1 text-sm md:text-base font-medium text-slate-800 whitespace-pre-line">
            {reflectionPrompt}
          </p>
        </div>
      )}
    </>
  );
}

function defaultHeadingFor(type) {
  return {
    intro: "Let's start",
    concept: "The big idea",
    example: "In real life",
    activity: "Try it",
    check: "Check your understanding",
    recap: "What you learned",
  }[type] || "Lesson";
}

function SectionEmoji({ type }) {
  const map = { intro: "✨", concept: "💡", example: "🌍", activity: "🎯", check: "🧠", recap: "📌" };
  return <span aria-hidden className="text-2xl">{map[type] || "📘"}</span>;
}

// Highlight key terms inline (case-insensitive first-occurrence match)
// keeping this simple: split body on term regex, wrap match in <button>.
function highlightTerms(text, terms, onClick) {
  if (!terms?.length || !text) return text;
  const parts = [];
  let rest = text;
  const seen = new Set();
  for (const kt of terms) {
    const term = kt.term;
    if (!term || seen.has(term.toLowerCase())) continue;
    const idx = rest.toLowerCase().indexOf(term.toLowerCase());
    if (idx < 0) continue;
    seen.add(term.toLowerCase());
    parts.push(rest.slice(0, idx));
    parts.push({ term: kt, matched: rest.slice(idx, idx + term.length) });
    rest = rest.slice(idx + term.length);
  }
  parts.push(rest);
  return parts.map((p, i) => {
    if (typeof p === "string") return <React.Fragment key={i}>{p}</React.Fragment>;
    return (
      <button
        key={i}
        type="button"
        onClick={() => onClick(p.term)}
        data-testid={`lesson-term-${p.term.term?.replace(/\W+/g, "-")}`}
        // Physical highlighter marker — a bold yellow swatch with a
        // slight rotate so it reads as "hand-marked" rather than a
        // rendered underline. See design_guidelines.json → Lesson
        // Reader → term highlight.
        className="inline-block rotate-[-1deg] bg-yellow-300 text-slate-900 px-1.5 py-0.5 rounded-sm font-bold border-b-2 border-yellow-500 hover:bg-yellow-400 hover:rotate-[1deg] transition-transform"
      >
        {p.matched}
      </button>
    );
  });
}

function TermPopover({ term, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="term-popover"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl border-4 border-slate-900 bg-white p-5 shadow-[10px_10px_0_0_rgba(15,23,42,1)]"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Key term</p>
            <h3 className="font-heading text-2xl font-bold" data-testid="term-title">{term.term}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={20} strokeWidth={3} />
          </button>
        </div>
        <p className="mt-3 text-base md:text-lg text-slate-800 font-medium">{term.definition}</p>
      </motion.div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Interactive check questions
// ────────────────────────────────────────────────────────────────────
function CheckQuestions({ questions, onComplete, subject = "" }) {
  const [answers, setAnswers] = useState({}); // idx → { chosen, correct }
  const answered = Object.keys(answers).length;
  const total = questions.length;
  const score = Object.values(answers).filter((a) => a.correct).length;

  // Feb 2026 · Personalization state — only rendered when the child
  // struggles (score < 60%). One tap fires the adaptive-explain call.
  const [explainState, setExplainState] = useState({ status: "idle", data: null });
  const [helpfulVerdict, setHelpfulVerdict] = useState(null);
  const struggled = answered === total && total > 0 && score / total < 0.6;

  const requestExplain = async () => {
    setExplainState({ status: "loading", data: null });
    try {
      const first = questions?.[0]?.q || "";
      const ctx = (first || "").slice(0, 900);
      const res = await explainAnotherWay({
        skill: subject || "concept",
        subject: subject || "",
        ageTier: (typeof document !== "undefined" && document.documentElement.getAttribute("data-age") === "little")
          ? "seedling"
          : "explorer",
        sectionText: ctx,
        whatWentWrong: `I only got ${score} of ${total} right.`,
      });
      if (res.ok) setExplainState({ status: "ok", data: res.data });
      else setExplainState({ status: "fallback", data: null });
    } catch {
      setExplainState({ status: "fallback", data: null });
    }
  };
  const markHelpful = (verdict) => {
    setHelpfulVerdict(verdict);
    recordPersonalizationHelpful({ skill: subject || "concept", subject, verdict });
  };

  useEffect(() => {
    if (answered === total && total > 0 && typeof onComplete === "function") {
      onComplete(score);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered, total]);

  // Feb 2026 · Emergent Command · Learning Strategy Upgrade (2).
  // Concept-visible "why" sentence — computed once after the child
  // finishes all questions. Falls back through skill → subject →
  // generic so the reveal is never blank.
  const whyLine = answered === total && total > 0
    ? whyLineFor({ skill: subject || "", subject: subject || "" })
    : null;

  return (
    <div className="mt-4 space-y-4" data-testid="check-questions">
      {questions.map((q, i) => (
        <div key={i} className="rounded-2xl border-[3px] border-slate-900 bg-sky-50 p-3 md:p-4">
          <ReadableText kind="question" text={`Question ${i + 1}. ${q.q}`} as="div" className="block">
            <p className="font-bold text-base md:text-lg">{i + 1}. {q.q}</p>
          </ReadableText>
          <div className="mt-2 grid gap-2">
            {(q.options || []).map((opt, oi) => {
              const state = answers[i];
              const isChosen  = state?.chosen === oi;
              const isCorrect = state && oi === q.correct_index;
              const showColor = state && (isChosen || isCorrect);
              return (
                <button
                  key={oi}
                  type="button"
                  disabled={!!state}
                  onClick={() =>
                    setAnswers((a) => ({ ...a, [i]: { chosen: oi, correct: oi === q.correct_index } }))
                  }
                  data-testid={`check-${i}-opt-${oi}`}
                  className={`text-left rounded-xl border-2 border-slate-900 p-2.5 font-bold text-sm md:text-base shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-colors ${
                    !showColor ? "bg-white" :
                    isCorrect  ? "bg-emerald-200" :
                    isChosen   ? "bg-rose-100"   : "bg-white"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {answers[i] && (
            <p className="mt-2 text-sm font-bold text-slate-700" data-testid={`check-${i}-feedback`}>
              {answers[i].correct
                ? "⭐ Nice — that's right! "
                : "🌱 Not quite — here's why… "}
              {q.explanation}
            </p>
          )}
        </div>
      ))}
      {answered === total && (
        <>
          <p className="text-sm font-bold text-slate-700">You got {score} of {total} — great effort! 💪</p>
          {whyLine && (
            <p
              className="mt-2 text-sm md:text-base font-bold text-fuchsia-900 bg-fuchsia-50 border-2 border-fuchsia-300 rounded-xl px-3 py-2 flex items-start gap-2"
              data-testid="check-questions-why-line"
            >
              <span aria-hidden className="text-base leading-none mt-0.5">🧭</span>
              <span>{whyLine}</span>
            </p>
          )}

          {/* Feb 2026 · Emergent Command · Personalization capability 1.
              "Explain another way" — only surfaced when the child
              struggled (< 60% correct), so it never nudges kids who
              already understand. One tap, warm Compass voice, safe
              fallback. */}
          {(struggled || explainState.status !== "idle") && (
            <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-sky-50 p-3" data-testid="explain-another-way-panel">
              {explainState.status === "idle" && (
                <button
                  type="button"
                  onClick={requestExplain}
                  data-testid="explain-another-way-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
                >
                  🧭 Explain another way
                </button>
              )}
              {explainState.status === "loading" && (
                <p className="text-sm font-bold text-slate-700 flex items-center gap-2" data-testid="explain-loading">
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-slate-900 border-t-transparent animate-spin" aria-hidden />
                  Compass is thinking of a different way to explain this…
                </p>
              )}
              {explainState.status === "fallback" && (
                <p className="text-sm font-bold text-slate-700" data-testid="explain-fallback">
                  Compass couldn&apos;t make a fresh explanation this time — no worries, try the check questions again or move on. The main lesson still fits.
                </p>
              )}
              {explainState.status === "ok" && explainState.data && (
                <div data-testid="explain-content">
                  <p className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-fuchsia-800">
                    <span aria-hidden>🧭</span> Compass · another way to see it
                  </p>
                  <p className="mt-1 font-heading text-base md:text-lg font-black text-slate-900" data-testid="explain-headline">
                    {explainState.data.headline}
                  </p>
                  <p className="mt-1 text-sm md:text-base font-bold text-slate-800" data-testid="explain-explanation">
                    {explainState.data.explanation}
                  </p>
                  <p className="mt-1.5 text-xs md:text-sm font-bold text-slate-700 italic" data-testid="explain-analogy">
                    <span aria-hidden>💡 </span>{explainState.data.analogy}
                  </p>
                  {explainState.data.micro_check?.q && (
                    <div className="mt-2 rounded-xl border-2 border-slate-900 bg-white p-2.5" data-testid="explain-micro-check">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Quick check</p>
                      <p className="mt-0.5 text-sm font-bold text-slate-900">{explainState.data.micro_check.q}</p>
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs font-bold text-emerald-700">Show answer</summary>
                        <p className="mt-1 text-sm font-bold text-slate-800">{explainState.data.micro_check.a}</p>
                      </details>
                    </div>
                  )}
                  {helpfulVerdict === null ? (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="explain-helpful-controls">
                      <span className="text-[11px] font-bold text-slate-600">Did that help?</span>
                      <button
                        type="button"
                        onClick={() => markHelpful(true)}
                        data-testid="explain-helpful-yes"
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full border-2 border-slate-900 bg-emerald-200 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]"
                      >
                        Yes, it clicked
                      </button>
                      <button
                        type="button"
                        onClick={() => markHelpful(false)}
                        data-testid="explain-helpful-no"
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]"
                      >
                        Not really
                      </button>
                    </div>
                  ) : (
                    <p className="mt-2 text-[11px] font-bold text-emerald-700" data-testid="explain-helpful-thanks">
                      Thanks — Compass will remember that.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  End-of-lesson panel (recap + CTA row + Goes deeper)
// ────────────────────────────────────────────────────────────────────
function EndPanel({ lesson, code, navigate, showDeeper, setShowDeeper }) {
  const already = isMastered(code);
  const journeyNext = lesson ? journeyNextFor(code, lesson.subject, "learn") : null;
  const nextInfo = useNextLesson(code);
  const nextTo = journeyNext || nextInfo?.next?.route || nextInfo?.fallback || (lesson?.subject ? `/subjects/${lesson.subject}` : "/subjects");
  const [gateOpen, setGateOpen] = useState(false);
  const [gateVerified, setGateVerified] = useState(false);
  const isGeography = (lesson?.subject || "").toLowerCase() === "geography";
  const link = (() => { try { return getClassLink() || {}; } catch { return {}; } })();
  // Feb 2026 · Emergent Command · Learning Strategy Upgrade (2).
  // Concept-visible whyLine — computed once on mount so every child
  // who reaches the end of a lesson literally sees the idea named.
  const endWhyLine = whyLineFor({ skill: "", subject: lesson?.subject || "" });
  const [rated, setRated] = useState(() => {
    try { return localStorage.getItem(`wq_kui:${code}`) || null; } catch { return null; }
  });
  const markLearned = () => {
    // Log an implicit "check passed" attempt so it counts toward mastery.
    recordAttempt(code, 5, 5);
    // Cycle 60 · Advance the lesson stage to REWARD (6/6) so the
    // Continue card shows 100 % (and can retire the tile).
    setLessonStage(code, LESSON_STAGES.REWARD);
    try {
      const strand = (lesson?.subject || "").toLowerCase() === "maths" ? "arithmetic"
        : (lesson?.subject || "").toLowerCase() === "english" ? "reading-writing"
        : "applied";
      trackCompletedActivity({
        activity_id: `lesson/${code}`,
        strand,
        result: "mastered",
      });
    } catch { /* ignore */ }
    toast.success("Nice work — added to your journey!");
  };
  const openGate = () => setGateOpen(true);
  const onGatePass = () => {
    setGateVerified(true);
    // Also record local mastery so isMastered() returns true.
    recordAttempt(code, 5, 5);
    // Migrate any legacy unverified self-report on this lesson to verified.
    try {
      const raw = localStorage.getItem("wq_unverified_lessons") || "[]";
      const arr = JSON.parse(raw);
      localStorage.setItem("wq_unverified_lessons",
        JSON.stringify(arr.filter((c) => c !== code)));
    } catch { /* ignore */ }
  };
  const submitRating = async (choice) => {
    if (rated) return;
    setRated(choice);
    try { localStorage.setItem(`wq_kui:${code}`, choice); } catch { /* ignore */ }
    try {
      const anonId = (() => {
        try {
          let id = localStorage.getItem("wq_anon_id");
          if (!id) {
            id = "anon-" + Math.random().toString(36).slice(2, 12);
            localStorage.setItem("wq_anon_id", id);
          }
          return id;
        } catch { return null; }
      })();
      // Cycle 7 wiring · include class + child ids when the learner
      // has joined a class so ratings feed the teacher heatmap.
      const link = getClassLink() || {};
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lessons/${encodeURIComponent(code)}/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          age_tier: lesson.age_tier || "explorer",
          rating: choice,
          anon_id: anonId,
          class_id: link.class_id || null,
          profile_id: link.child_id || null,
        }),
      });
      toast.success("Thanks for telling us!");
    } catch {
      // silent — the local flag keeps the UI in a good state
    }
  };
  const CHOICES = [
    { key: "confused", label: "Confused", face: "😕", bg: "bg-rose-200" },
    { key: "okay",     label: "Okay",     face: "😐", bg: "bg-amber-200" },
    { key: "loved",    label: "Loved it", face: "🤩", bg: "bg-emerald-200" },
  ];
  return (
    <section className="mt-6 rounded-3xl border-4 border-slate-900 bg-emerald-100 p-4 md:p-6 shadow-[6px_6px_0_0_rgba(15,23,42,1)]" data-testid="lesson-end">
      <div className="flex items-baseline gap-2">
        <span aria-hidden className="text-2xl">🎉</span>
        <h3 className="font-heading text-xl md:text-2xl font-bold">You reached the end!</h3>
      </div>

      {/* Feb 2026 · Concept-visible "why" reveal at end-of-lesson.
          Every lesson finish shows the one-sentence idea the child
          just met, in Compass's voice. Falls back through subject →
          generic so it's never blank. */}
      {endWhyLine && (
        <p
          className="mt-3 text-sm md:text-base font-bold text-fuchsia-900 bg-fuchsia-50 border-2 border-fuchsia-300 rounded-xl px-3 py-2 flex items-start gap-2"
          data-testid="lesson-end-why-line"
        >
          <span aria-hidden className="text-base leading-none mt-0.5">🧭</span>
          <span>{endWhyLine}</span>
        </p>
      )}

      {/* Cycle 7 · Kid-Understood-It rating */}
      <div className="mt-4 rounded-2xl border-[3px] border-slate-900 bg-white p-3 md:p-4 shadow-[0_3px_0_0_rgba(15,23,42,1)]" data-testid="kui-panel">
        <p className="font-heading font-bold text-slate-900 text-sm md:text-base">
          How did this lesson feel?
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {CHOICES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => submitRating(c.key)}
              disabled={!!rated}
              data-testid={`kui-${c.key}`}
              aria-pressed={rated === c.key}
              className={
                "inline-flex items-center gap-2 h-12 px-4 rounded-full border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] transition-transform " +
                (rated === c.key ? c.bg + " ring-2 ring-slate-900" : (rated ? "bg-white opacity-40" : c.bg)) +
                " active:translate-y-[2px] active:shadow-none"
              }
            >
              <span aria-hidden className="text-xl leading-none">{c.face}</span>
              <span>{c.label}</span>
            </button>
          ))}
        </div>
        {rated && (
          <p className="mt-2 text-xs font-bold text-slate-500" data-testid="kui-ack">
            Thanks — your teacher and My World Quest will use this to improve lessons.
          </p>
        )}
      </div>

      {lesson.real_world_link && (
        <div className="mt-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">In real life</p>
          <p className="text-sm md:text-base font-medium text-slate-800">{lesson.real_world_link}</p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {isGeography ? (
          <button
            type="button"
            onClick={openGate}
            data-testid="lesson-verify-quiz"
            className={`inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] ${
              gateVerified || already ? "bg-emerald-300" : "bg-yellow-300"
            }`}
          >
            <CheckCircle2 size={16} strokeWidth={3} />
            {(gateVerified || already) ? "Verified ✓" : "Verify with a quiz"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => { if (!already) markLearned(); }}
            data-testid="lesson-mark-learned"
            disabled={already}
            className={"inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] " + (already ? "bg-emerald-200 cursor-default" : "bg-yellow-300")}
          >
            <CheckCircle2 size={16} strokeWidth={3} /> {already ? "Learned ✓" : "Mark as learned"}
          </button>
        )}
        <Link
          to={`/knowledge`}
          data-testid="lesson-practice-link"
          className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]"
        >
          🎮 Practice with a game
        </Link>
        <Link
          to={`/live/build?${new URLSearchParams({
            src: "topic",
            ...(lesson.linked_topic_id ? { topic_id: lesson.linked_topic_id } : {}),
            tier: lesson.age_tier || "explorer",
            ...(lesson.linked_curriculum ? { curriculum: (
              lesson.linked_curriculum.toLowerCase().includes("uk") ? "uk" :
              lesson.linked_curriculum.toLowerCase().includes("cambridge") ? "cambridge" :
              lesson.linked_curriculum.toLowerCase().includes("cbse") ? "cbse" :
              lesson.linked_curriculum.toLowerCase().includes("uae") ? "uae_moe" :
              lesson.linked_curriculum.toLowerCase().includes("ib") ? "ib" :
              lesson.linked_curriculum.toLowerCase().includes("common") ? "common_core" : "any"
            ) } : {}),
          }).toString()}`}
          data-testid="lesson-live-quest-link"
          className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-fuchsia-200 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]"
          title={lesson.linked_topic_id ? `Live Quest on: ${lesson.linked_topic_id}` : "Build a Live Quest"}
        >
          📡 Play as Live Quest
        </Link>
        <Link
          to={nextTo}
          data-testid="lesson-next-link"
          title={nextInfo?.next?.title || undefined}
          className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-sky-200 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]"
        >
          {journeyNext ? "→ Next in your journey" : nextInfo?.next ? `→ Next: ${String(nextInfo.next.title).slice(0, 40)}` : nextInfo?.stage_complete ? "✓ Stage done — all topics" : "→ Next lesson"}
        </Link>
      </div>
      {gateOpen && (
        <GeographyGate
          lessonId={code}
          tier={lesson.age_tier || "explorer"}
          childId={link.child_id || null}
          classId={link.class_id || null}
          onClose={() => setGateOpen(false)}
          onPass={onGatePass}
        />
      )}

      {lesson.goes_deeper && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowDeeper((v) => !v)}
            data-testid="lesson-toggle-deeper"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]"
          >
            {showDeeper ? "Hide" : "Want more? 🚀"}
          </button>
          {showDeeper && (
            <div className="mt-2 rounded-2xl border-[3px] border-slate-900 bg-white p-3 md:p-4" data-testid="lesson-deeper">
              <p className="whitespace-pre-line text-sm md:text-base text-slate-800">
                {lesson.goes_deeper}
              </p>
            </div>
          )}
        </div>
      )}

      {lesson.sources_note && (
        <p className="mt-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
          {lesson.sources_note}
        </p>
      )}

      {/* Human Support Escalation Policy (Jun 2026): no persistent tutor banner on child-facing lessons.
          Human support is only surfaced to grown-ups via the Parent Dashboard when HUMAN_SUPPORT_ELIGIBLE. */}
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────
//  CompletionScreen — Command · Completion & Lesson Navigation · Phase 1
//  The lesson's final card (index === contentTotal). It is the LAST thing
//  in the lesson document — nothing renders after it. Exactly 6 elements,
//  in order:
//    1. Animated tick (respects prefers-reduced-motion)
//    2. Objective restated as achieved
//    3. Progress delta
//    4. IN REAL LIFE action prompt
//    5. ONE reflection widget (the 4-option ReflectionPanel)
//    6. ONE primary next action (+ at most two quieter secondary actions)
//  Reward matches performance: finishing the cards earns the lesson tick
//  (no stars, no confetti). Mastery is separate (computed from practice).
// ────────────────────────────────────────────────────────────────────
function CompletionScreen({ lesson, code, tier, contentTotal, academyMatch, navigate, onReplay }) {
  const reduce = useReducedMotion();
  const achieved = achievedStatement(lesson);
  // Same stored readiness source as the Learn Topics cards (no drift): Practice/Test chips only when content exists.
  const readiness = useReadiness();
  const audit = readiness.get(code, lesson?.age_tier || tier);
  const practiceTo = audit.known ? (audit.practice ? `/practice/${code}` : academyMatch?.to || null) : (academyMatch?.to || "/knowledge");
  const testTo = audit.known && audit.test && (lesson?.age_tier || tier) !== "seedling" ? `/learn-topics/${code}/test` : null;
  const journeyNext = lesson ? journeyNextFor(code, lesson.subject, "learn") : null;
  const nextInfo = useNextLesson(code);
  const nextTo = journeyNext || nextInfo?.next?.route || nextInfo?.fallback || (lesson?.subject ? `/subjects/${lesson.subject}` : "/subjects");
  // Related game from the canonical game catalogue (same ids/routes as Play) — optional, never a lock.
  const relatedGame = lesson ? activitiesFor(String(lesson.subject || "").toLowerCase())[0] || null : null;

  // Record "finished" once — the tick earned by completing the cards.
  // Uses the existing stage store (no new schema in Phase 1). NOT mastery,
  // NOT stars, NOT confetti.
  useEffect(() => {
    if (!code) return;
    try { setLessonStage(code, LESSON_STAGES.MASTERY); } catch { /* ignore */ }
    try {
      const strand = (lesson?.subject || "").toLowerCase() === "maths" ? "arithmetic"
        : (lesson?.subject || "").toLowerCase() === "english" ? "reading-writing" : "applied";
      trackCompletedActivity({ activity_id: `lesson/${code}`, strand, result: "finished" });
    } catch { /* ignore */ }
    try { trackEvent("lesson_finished", { code, subject: lesson?.subject || "" }); } catch { /* ignore */ }
    emitFutureReadyEvent({ eventType: "lessonCompleted", activityId: code, lessonId: code, subject: lesson?.subject, ageTier: lesson?.age_tier || tier, source: "LESSON" });
    reportSupport(code, "alternate_explanation", String(lesson?.subject || "").toLowerCase()); // re-reading after difficulty = automated repair
    funnel("lesson_completed", { subject: String(lesson?.subject || "").toLowerCase(), objective: code, activityType: "lesson", route: `/lesson/${code}` });
    juice("complete", { label: lesson?.title ? String(lesson.title).slice(0, 40) : "Lesson" });
  }, [code, lesson]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      className="rounded-3xl border-4 border-slate-900 bg-emerald-50 p-6 md:p-8 shadow-[6px_6px_0_0_rgba(15,23,42,1)] max-w-[75ch] mx-auto text-center"
      data-testid="lesson-completion"
    >
      {/* 1 · Animated tick */}
      <motion.div
        initial={reduce ? false : { scale: 0, rotate: -20 }}
        animate={reduce ? {} : { scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 16 }}
        className="mx-auto w-20 h-20 rounded-full bg-emerald-400 border-4 border-slate-900 grid place-items-center shadow-[0_4px_0_0_rgba(15,23,42,1)]"
        data-testid="completion-tick"
        aria-hidden
      >
        <CheckCircle2 size={44} strokeWidth={3} className="text-white" />
      </motion.div>

      {/* 2 · Objective restated as achieved */}
      <h2 className="mt-5 font-heading text-2xl md:text-3xl font-black text-slate-900" data-testid="completion-achieved">
        {achieved}
      </h2>

      {/* 3 · Progress delta */}
      <p className="mt-2 text-sm md:text-base font-bold text-emerald-800" data-testid="completion-delta">
        You worked through all {contentTotal} {contentTotal === 1 ? "card" : "cards"}. Lesson finished ✓
      </p>

      {/* 4 · IN REAL LIFE action prompt */}
      {lesson.real_world_link && (
        <div className="mt-5 rounded-2xl border-[3px] border-slate-900 bg-white p-3 md:p-4 text-left" data-testid="completion-real-life">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">In real life</p>
          <p className="text-sm md:text-base font-medium text-slate-800">{lesson.real_world_link}</p>
        </div>
      )}

      {/* 5 · ONE reflection widget (4-option) */}
      <div className="mt-5 text-left">
        <ReflectionPanel
          code={lesson.code || code}
          outcome={lesson.outcome || lesson.title}
          subject={lesson.subject}
          ageTier={lesson.age_tier}
        />
      </div>

      {/* 6 · ONE primary action + two quieter secondary actions */}
      <div className="mt-6 flex flex-col items-center gap-3">
        <Link
          to={nextTo}
          data-testid="completion-next-lesson"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border-[3px] border-slate-900 bg-amber-300 font-heading font-black text-base shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:-translate-y-[1px] transition-transform"
        >
          {journeyNext ? "Next in your journey" : nextInfo?.next ? `Next lesson: ${String(nextInfo.next.title).slice(0, 36)}` : nextInfo?.stage_complete ? "Stage complete — see all topics" : "Next lesson"} <ChevronRight size={18} strokeWidth={3} />
        </Link>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="completion-save"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-300 bg-white font-bold text-xs text-slate-600 hover:border-slate-400 transition-colors"
          >
            💾 Save this lesson
          </button>
          {practiceTo && (
          <Link
            to={practiceTo}
            data-testid="completion-practice"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-300 bg-white font-bold text-xs text-slate-600 hover:border-slate-400 transition-colors"
          >
            🎯 Practise
          </Link>
          )}
          {relatedGame && (
          <Link
            to={relatedGame.to}
            onClick={() => track("lesson_related_game_opened", { code, game: relatedGame.id })}
            data-testid="completion-play-game"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-300 bg-white font-bold text-xs text-slate-600 hover:border-slate-400 transition-colors"
          >
            🎮 Play {relatedGame.label}
          </Link>
          )}
          {testTo && (
          <Link
            to={testTo}
            data-testid="completion-test"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border-2 border-slate-300 bg-white font-bold text-xs text-slate-600 hover:border-slate-400 transition-colors"
          >
            🧠 Check
          </Link>
          )}
        </div>
        <button
          type="button"
          onClick={onReplay}
          data-testid="completion-replay"
          className="text-xs font-semibold text-slate-400 hover:text-slate-600 underline underline-offset-2"
        >
          Do it again?
        </button>
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────
//  Illustration slot — zero-cost collage.
//
//  Renders a colourful gradient card with 4 topic-relevant emojis
//  arranged in a decorative layout. We would love to serve real
//  generated images (Gemini Nano Banana) here, but running one image
//  gen per lesson section blows the Universal LLM key budget on large
//  curriculum batches. This collage looks great, is deterministic, and
//  never fails.
//
//  If `illustrationUrl` is ever populated by the backend (real image
//  pipeline), it takes priority — this component gracefully upgrades.
// ────────────────────────────────────────────────────────────────────
function IllustrationSlot({ prompt, type, heading, keyTerms, illustrationUrl }) {
  const rule = pickIllustration({ prompt, type, heading, keyTerms });
  return (
    <figure
      className="mt-4 rounded-2xl border-[3px] border-slate-900 overflow-hidden shadow-[0_3px_0_0_rgba(15,23,42,1)]"
      data-testid="lesson-illustration"
    >
      {illustrationUrl ? (
        <img
          src={illustrationUrl}
          alt={heading || prompt || "Lesson illustration"}
          className="w-full aspect-[16/9] object-cover"
          loading="lazy"
        />
      ) : (
        <div
          className={`relative aspect-[16/9] w-full bg-gradient-to-br ${rule.grad} overflow-hidden`}
        >
          {/* Softer blob layers give the collage a hand-painted feel. */}
          <span aria-hidden className="pointer-events-none absolute -top-8 -left-8 h-40 w-40 rounded-full bg-white/40 blur-2xl" />
          <span aria-hidden className="pointer-events-none absolute -bottom-10 -right-10 h-48 w-48 rounded-full bg-white/30 blur-2xl" />

          {/* Four emojis in decorative positions — sizes vary so it doesn't
              look like a uniform grid. Motion is CSS-only, no framer here
              (this component renders inside a section list). */}
          <motion.span
            aria-hidden
            className="absolute left-4 top-3 text-5xl md:text-6xl select-none drop-shadow-sm"
            animate={{ y: [0, -6, 0], rotate: [0, -4, 0] }}
            transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
          >{rule.emojis[0]}</motion.span>
          <motion.span
            aria-hidden
            className="absolute right-6 top-6 text-4xl md:text-5xl select-none drop-shadow-sm"
            animate={{ y: [0, -4, 0], rotate: [0, 6, 0] }}
            transition={{ duration: 5.0, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          >{rule.emojis[1]}</motion.span>
          <motion.span
            aria-hidden
            className="absolute left-8 bottom-3 text-5xl md:text-6xl select-none drop-shadow-sm"
            animate={{ y: [0, -5, 0], rotate: [0, 8, 0] }}
            transition={{ duration: 4.6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
          >{rule.emojis[2]}</motion.span>
          <motion.span
            aria-hidden
            className="absolute right-3 bottom-6 text-6xl md:text-7xl select-none drop-shadow"
            animate={{ y: [0, -8, 0], rotate: [0, -8, 0] }}
            transition={{ duration: 5.4, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
          >{rule.emojis[3]}</motion.span>

          {/* Center heading badge (only if heading is short enough) */}
          {heading && heading.length < 34 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="font-heading text-lg md:text-2xl font-bold text-slate-900 bg-white/80 backdrop-blur px-3 py-1.5 rounded-xl border-2 border-slate-900 shadow-[0_3px_0_0_rgba(15,23,42,1)] text-center max-w-[80%]">
                {heading}
              </p>
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Read-aloud button — pipes plain text through /api/tts (same as
//  AITeacher).
// ────────────────────────────────────────────────────────────────────
function ReadAloudButton({ text }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(false);
  };

  const play = async () => {
    if (playing) { stop(); return; }
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 900) }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      setPlaying(true);
      audio.onended = () => { setPlaying(false); audioRef.current = null; };
      await audio.play();
    } catch {
      setPlaying(false);
      toast.error("Read aloud isn't available right now.");
    }
  };

  return (
    <button
      type="button"
      onClick={play}
      data-testid="lesson-read-aloud"
      className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-2 border-slate-900 bg-sky-200 font-bold text-sm shadow-[0_2px_0_0_rgba(15,23,42,1)]"
    >
      {playing ? <VolumeX size={18} strokeWidth={3} /> : <Volume2 size={18} strokeWidth={3} />}
      {playing ? "Stop" : "Read aloud"}
    </button>
  );
}


// Feb 2026 · Simple menu-row primitive for the lesson-actions overflow
// popover on mobile. Kept local (not a shared component) because it's
// only used inside LessonReader today.
function MenuButton({ onClick, testId, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-2 h-10 px-3 rounded-xl text-left font-bold text-sm text-slate-900 hover:bg-yellow-100"
    >
      <span aria-hidden className="text-base">{icon}</span>
      {children}
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────
//  Present mode overlay — fullscreen, big-type, page-by-page deck
//  view of the current lesson, powered by lib/lessonDeck.js. Exits
//  via ESC, the corner Exit button, or swipe-down on mobile.
// ────────────────────────────────────────────────────────────────────
function PresentOverlay({ lesson, step, onClose }) {
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);

  // Build a lesson-deck-shaped payload from the reader's sections.
  const deckLesson = useMemo(() => {
    const sections = Array.isArray(lesson?.sections) ? lesson.sections : [];
    return {
      id: lesson?.code || lesson?.id || "lesson",
      title: lesson?.title || "Lesson",
      subject: lesson?.subject || "",
      uk_year: lesson?.uk_year,
      pages: sections.map((s, i) => ({
        title: s.heading || defaultHeadingFor(SECTION_TYPE_ALIASES[s.type] || s.type || "concept") || `Page ${i + 1}`,
        body: s.body || s.prompt || s.worked_example || s.instructions || "",
      })),
    };
  }, [lesson]);

  // Write the deck HTML into the iframe once on mount / lesson change.
  useEffect(() => {
    if (!iframeRef.current || !deckLesson) return;
    const slides = presentationSlides(deckLesson);
    const html = renderDeckHtml(deckLesson, slides, { kidMode: true, readAloud: true });
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${(deckLesson.title || "Lesson").replace(/</g, "&lt;")}</title></head><body>${html}</body></html>`);
    doc.close();
    try { trackEvent("lesson_present_opened", { code: deckLesson.id, step }); } catch { /* ignore */ }
  }, [deckLesson, step]);

  // ESC-to-exit + request fullscreen on open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onMsg = (e) => {
      if (e && e.data && e.data.type === "mwq-deck-close") onClose();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("message", onMsg);
    // Ask the browser for fullscreen on the wrapper. Silently ignored
    // when the browser refuses (e.g. no user gesture in some browsers).
    const el = wrapRef.current;
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(() => { /* ignore */ });
    }
    // Lock body scroll while present mode is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("message", onMsg);
      document.body.style.overflow = prev;
      if (document.fullscreenElement) {
        try { document.exitFullscreen(); } catch { /* ignore */ }
      }
    };
  }, [onClose]);

  // Swipe-down-to-exit on mobile. Threshold 90 px, vertical dominance.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    let sy = null;
    let sx = null;
    const onStart = (e) => {
      if (!e.touches || !e.touches[0]) return;
      sy = e.touches[0].clientY;
      sx = e.touches[0].clientX;
    };
    const onEnd = (e) => {
      if (sy == null || !e.changedTouches || !e.changedTouches[0]) { sy = null; return; }
      const dy = e.changedTouches[0].clientY - sy;
      const dx = e.changedTouches[0].clientX - sx;
      sy = null; sx = null;
      if (dy > 90 && Math.abs(dy) > Math.abs(dx) * 1.5) onClose();
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEnd);
    };
  }, [onClose]);

  return (
    <div
      ref={wrapRef}
      data-testid="lesson-present-overlay"
      className="fixed inset-0 z-[100] bg-slate-950"
      role="dialog"
      aria-modal="true"
      aria-label="Lesson present mode"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lesson-present-exit"
        aria-label="Exit present mode"
        className="fixed top-3 left-3 z-[110] inline-flex items-center gap-1 h-10 px-3 rounded-full border-2 border-slate-900 bg-yellow-300 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
      >
        <X size={14} strokeWidth={3} /> Exit
      </button>
      <p
        className="md:hidden fixed bottom-3 left-1/2 -translate-x-1/2 z-[110] text-[10px] font-black uppercase tracking-widest text-white/70 pointer-events-none"
        aria-hidden
      >
        Swipe down to exit
      </p>
      <iframe
        ref={iframeRef}
        title="Lesson present deck"
        data-testid="lesson-present-frame"
        className="w-full h-full border-0"
        style={{ background: "#FBF7EE" }}
      />
    </div>
  );
}
